import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { TopDividendHolding, PaginationInfo } from "../Charts/hooks/useDividendAnalysis";
import { CompanyIdentity, companyCellSx } from "../../shared/CompanyIdentity";
import { glassMorphism, subtleBackground, greenGradient } from "../../../theme";

interface Column {
  id: keyof TopDividendHolding | 'company';
  label: string;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, holding: TopDividendHolding, currencySymbol?: string) => React.ReactNode;
}

const getColumns = (currencySymbol: string): Column[] => [
  {
    id: 'company',
    label: 'Company',
    align: 'left',
    format: (_, holding) => (
      <CompanyIdentity
        company={holding.companyName}
        symbol={holding.ticker}
        image={holding.logo}
      />
    ),
  },
  {
    id: 'shares',
    label: 'Shares',
    align: 'right',
    format: (value) => (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {Number(value).toLocaleString()}
      </Typography>
    ),
  },
  {
    id: 'currentValue',
    label: 'Value',
    align: 'right',
    format: (value) => (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {currencySymbol}{Number(value).toLocaleString()}
      </Typography>
    ),
  },
  {
    id: 'dividendYield',
    label: 'Dividend Yield',
    align: 'center',
    format: (value) => (
      <Chip
        label={`${Number(value).toFixed(2)}%`}
        size="small"
        sx={{
          backgroundColor: value > 3 ? "rgba(34, 197, 94, 0.1)" : value > 1 ? "rgba(59, 130, 246, 0.1)" : "rgba(156, 163, 175, 0.1)",
          color: value > 3 ? "#22c55e" : value > 1 ? "#3b82f6" : "#9ca3af",
          fontWeight: 600,
          border: "none",
        }}
      />
    ),
  },
  {
    id: 'annualDividendIncome',
    label: 'Annual Income',
    align: 'right',
    format: (value) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
        {currencySymbol}{Number(value).toFixed(2)}
      </Typography>
    ),
  },
  {
    id: 'monthlyDividendIncome',
    label: 'Monthly Income',
    align: 'right',
    format: (value) => (
      <Typography variant="body2" color="text.secondary">
        {currencySymbol}{Number(value).toFixed(2)}
      </Typography>
    ),
  },
];

interface TopDividendHoldingsTableProps {
  holdings: TopDividendHolding[];
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  isLoading?: boolean;
}

const TopDividendHoldingsTable: React.FC<TopDividendHoldingsTableProps> = ({
  holdings,
  pagination,
  onPageChange,
  onLimitChange,
  isLoading = false,
}) => {
  const currencySymbol = "$";
  const columns = getColumns(currencySymbol);
  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          ...glassMorphism,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  ...subtleBackground,
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      borderBottom: (theme) =>
                        theme.palette.mode === "dark"
                          ? "1px solid rgba(255,255,255,0.08)"
                          : "1px solid rgba(0,0,0,0.06)",
                      py: 1.5,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {holdings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {isLoading ? 'Loading top dividend holdings...' : 'No dividend-paying holdings found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                holdings.map((holding, index) => (
                  <TableRow
                    key={holding.ticker}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                        // REMOVED transform for performance: transform: 'translateY(-1px)',
                      },
                      '&:last-child td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          ...(column.id === 'company' ? { ...companyCellSx, width: '28%' } : {}),
                          borderBottom: (theme) =>
                            theme.palette.mode === "dark"
                              ? "1px solid rgba(255,255,255,0.04)"
                              : "1px solid rgba(0,0,0,0.03)",
                          py: 1.5,
                        }}
                      >
                        {column.format 
                          ? column.format(holding[column.id as keyof TopDividendHolding], holding)
                          : holding[column.id as keyof TopDividendHolding]
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {pagination.totalPayments > 0 
                ? ((pagination.currentPage - 1) * pagination.limit + 1)
                : 0} - {Math.min(
                  pagination.currentPage * pagination.limit,
                  pagination.totalPayments
                )} of {pagination.totalPayments} holdings
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pagination.limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0.5,
                      fontSize: '0.875rem',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
              
              <Typography variant="body2" color="text.secondary">
                per page
              </Typography>

              {pagination.totalPages > 1 && (
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={(_, page) => onPageChange(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                  sx={{
                    ml: 1,
                    '& .MuiPagination-ul': {
                      flexWrap: 'nowrap',
                    },
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      fontWeight: 500,
                      '&.Mui-selected': {
                        ...greenGradient,
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TopDividendHoldingsTable;